import { useRef, useState, type FormEvent } from "react";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db6c5pvqhq9pk5mecsd0";

const contactInfo = [
  {
    icon: "ri-mail-line",
    label: "合作邮箱",
    value: "partner@ccvideo.demo",
  },
  {
    icon: "ri-customer-service-2-line",
    label: "用户反馈",
    value: "support@ccvideo.demo",
  },
  {
    icon: "ri-phone-line",
    label: "联系电话",
    value: "400-000-2026（工作日 9:00-18:00）",
  },
  {
    icon: "ri-map-pin-line",
    label: "办公地址",
    value: "北京市朝阳区影视创意园 A 座 12 层",
  },
];

export default function ContactPage() {
  usePageMeta({
    title: "联系我们 - CC视频",
    description:
      "联系CC视频：欢迎通过表单或在下方联系方式与我们沟通内容合作、用户反馈与建议，我们会尽快回复。",
    keywords: "联系CC视频,CC视频联系,影视合作,用户反馈",
  });

  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">(
    "idle"
  );
  const [formError, setFormError] = useState("");
  const [submittedName, setSubmittedName] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === "submitting") return;

    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setSubmittedName(String(formData.get("name") ?? ""));
      setStatus("success");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const str = String(value).trim();
      if (str) payload.append(key, str);
    });

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam = typeof serverMsg === "string" && /spam/i.test(serverMsg);
      const ok =
        response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setSubmittedName(String(formData.get("name") ?? ""));
        setStatus("success");
        formRef.current?.reset();
      } else {
        setFormError(
          serverMsg && typeof serverMsg === "string"
            ? serverMsg
            : "提交失败，请稍后重试。"
        );
        setStatus("error");
      }
    } catch {
      setFormError("网络异常，提交失败，请稍后重试。");
      setStatus("error");
    }
  };

  return (
    <div className="max-w-content mx-auto px-4 md:px-6 py-6 md:py-8 pb-16 md:pb-20">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />

      <div className="mt-4 mb-8">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
          联系我们
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-500 max-w-3xl">
          无论是内容合作、用户反馈还是使用建议，都欢迎通过下方表单或联系方式与我们沟通，我们会尽快回复。
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-5">
              在线留言
            </h2>

            {status === "success" ? (
              <div className="rounded-lg bg-background-100 p-8 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-check-line text-3xl"></i>
                </div>
                <h3 className="mt-4 text-lg font-medium text-foreground-900">
                  提交成功
                </h3>
                <p className="mt-2 text-sm text-foreground-500">
                  {submittedName ? `${submittedName}，` : ""}
                  感谢你的留言，我们已收到，会尽快通过你留下的方式回复。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setStatus("idle");
                    setFormError("");
                  }}
                  className="mt-5 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  继续留言
                </button>
              </div>
            ) : (
              <form
                ref={formRef}
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div>
                  <label
                    htmlFor="contact-name"
                    className="block text-sm font-medium text-foreground-800 mb-2"
                  >
                    姓名 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    maxLength={50}
                    placeholder="请输入你的称呼"
                    className="w-full h-11 px-4 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
                  />
                </div>

                <div>
                  <label
                    htmlFor="contact-email"
                    className="block text-sm font-medium text-foreground-800 mb-2"
                  >
                    邮箱 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    maxLength={80}
                    placeholder="请输入你的邮箱，方便我们回复"
                    className="w-full h-11 px-4 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
                  />
                </div>

                <div>
                  <label
                    htmlFor="contact-message"
                    className="block text-sm font-medium text-foreground-800 mb-2"
                  >
                    留言内容 <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请输入你想告诉我们的内容（不超过500字）"
                    className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors resize-none"
                  ></textarea>
                </div>

                <div className="hp-field" aria-hidden="true">
                  <label htmlFor="contact-website-alt">请勿填写此字段</label>
                  <input
                    id="contact-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {status === "error" && formError && (
                  <div className="flex items-start gap-2 rounded-md bg-accent-50 border border-accent-200 p-3 text-sm text-accent-800">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    <span>{formError}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full sm:w-auto h-11 px-8 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-1.5"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      提交留言
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-lg bg-background-100 border border-background-200 p-6">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
              联系方式
            </h2>
            <ul className="space-y-4">
              {contactInfo.map((item) => (
                <li key={item.label} className="flex items-start gap-3">
                  <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-background-50 text-primary-600">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-400">{item.label}</p>
                    <p className="text-sm text-foreground-800 break-words">
                      {item.value}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg bg-primary-900 p-6">
            <h2 className="text-base font-heading font-bold text-background-50">
              合作与反馈
            </h2>
            <p className="mt-2 text-sm text-background-300 leading-relaxed">
              内容合作、版权相关或平台建议，均可通过左侧表单提交，我们会在工作日尽快处理。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}